/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Bounds, PanState } from '../diagram.types';
import { panXYToCenterXY } from './diagramUtils';
export type PanBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/**
 * The pan values that describes where the diagram content
 * is panned
 */
export type Pan = {
  panX: number;
  panY: number;
};

const constrainPan = (
  previousPan: Pan,
  nextPan: Pan,
  panBounds: PanBounds,
  panDirection: 'x' | 'y' | 'any'
) => {
  const panX = panDirection !== 'y' ? nextPan.panX : previousPan.panX;
  const panY = panDirection !== 'x' ? nextPan.panY : previousPan.panY;
  return {
    panX: panX > panBounds.maxX || panX < panBounds.minX ? previousPan.panX : panX,
    panY: panY > panBounds.maxY || panY < panBounds.minY ? previousPan.panY : panY
  };
};

export const pan = (
  previousPan: Pan,
  nextPan: Pan,
  panDirection: 'x' | 'y' | 'any',
  zoom: number,
  width: number,
  height: number,
  panBounds?: PanBounds,
  onPanChange?: (detail: PanState) => void
) => {
  if (!panBounds) {
    //On initial render before nodes are rendered
    return { newPan: { panX: 0, panY: 0 } };
  }
  const newPan = constrainPan(previousPan, nextPan, panBounds, panDirection);
  const isPanChanged = newPan.panX !== previousPan.panX || newPan.panY !== previousPan.panY;
  const center = panXYToCenterXY(newPan.panX, newPan.panY, zoom, width, height);
  isPanChanged &&
    onPanChange &&
    onPanChange({
      centerX: center.centerX,
      centerY: center.centerY
    });
  return { newPan };
};

export const getPanBounds = (
  width: number,
  height: number,
  elemBounds: Bounds,
  panType: string,
  zoom: number,
  minZoom: number,
  initPanZoomState: {
    zoom: number;
    centerX?: number;
    centerY?: number;
  }
): PanBounds => {
  const { x, y, w, h } = elemBounds;
  let minX, maxX, minY, maxY;
  if (panType === 'centerContent') {
    minX = width / 2 - (w + x) * zoom;
    minY = height / 2 - (h + y) * zoom;
    maxX = width / 2 - x * zoom;
    maxY = height / 2 - y * zoom;
  } else {
    // panType equals fixed
    const zoomRatio = zoom / minZoom;
    // Find left corner of the content at min zoom and content is centered
    const minZoomX = (width - w * minZoom) / 2 - x * minZoom;
    const minZoomY = (height - h * minZoom) / 2 - y * minZoom;
    // When we pan all the way to the right, the left corner of displayable area is 0,0 as in min zoom.
    // When we pan all the way to the left, lets find the corner of the displayable area
    const leftCornerX = width - width * zoomRatio;
    const leftCornerY = height - height * zoomRatio;
    // Now we have everything to calculate pan zoom constraints
    minX = leftCornerX + minZoomX * zoomRatio;
    minY = leftCornerY + minZoomY * zoomRatio;
    maxX = minZoomX * zoomRatio;
    maxY = minZoomY * zoomRatio;
  }
  const initZoom = initPanZoomState.zoom;
  const initCenterX = initPanZoomState.centerX;
  const initCenterY = initPanZoomState.centerY;
  // If an initial panZoomState is set, should expand pan bounds to include it
  let dx, dy;
  if (initCenterX) {
    const boundsX = initCenterX * initZoom - width / 2;
    if (-boundsX * initZoom < minX) {
      dx = minX - boundsX * initZoom;
      minX -= dx;
      maxX += dx;
    } else if (-boundsX * initZoom > maxX) {
      dx = -boundsX * initZoom - maxX;
      minX -= dx;
      maxX += dx;
    }
  }
  if (initCenterY) {
    const boundsY = initCenterY * initZoom - height / 2;
    if (-boundsY * initZoom < minY) {
      dy = minY - boundsY * initZoom;
      minY -= dy;
      maxY += dy;
    } else if (-boundsY * initZoom > maxY) {
      dy = -boundsY * initZoom - maxY;
      minY -= dy;
      maxY += dy;
    }
  }
  return {
    minX: minX,
    maxX: maxX,
    minY: minY,
    maxY: maxY
  };
};
